/**
 * ops2026 left-menu config (founder request 2026-10-06: the flat list
 * grew to 13 items; group it by job-to-be-done with collapsible parents).
 *
 * The menu is a list of entries. An entry is either a single link shown
 * at the top level, or a group of links under a collapsible parent.
 *
 * ADDING A PAGE: add one `{ id, label, icon }` line to the right group's
 * `items` below. Role visibility comes from `VIEW_MIN_ROLE` in
 * `lib/roles.ts` (add the view's minimum role there), and the route
 * comes from `View` in `app/view-router.tsx`. Nothing in `Sidebar.tsx`
 * needs to change. A group whose items are all hidden for a role is
 * hidden too.
 *
 * Routes are unchanged: each item's `id` is the same `?view=` value as
 * before, so bookmarks keep working.
 */

import {
  Activity,
  BarChart3,
  Bot,
  Building2,
  Clock,
  Flag,
  FlaskConical,
  HeartPulse,
  KeyRound,
  Mail,
  Package,
  ScrollText,
  ShieldCheck,
  Shield,
  SlidersHorizontal,
  Split,
  Trash2,
  Users,
  UserX,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import type { StaffRole } from '@sill/types/api';

import type { View } from '../../app/view-router.js';
import { canView } from '../../lib/roles.js';

export interface NavItem {
  readonly kind: 'item';
  /** The route (`?view=` value). */
  readonly id: View;
  readonly label: string;
  readonly icon: LucideIcon;
  /** Sub-pages that should highlight this item (e.g. a detail view). */
  readonly activeFor?: readonly View[];
}

export interface NavGroup {
  readonly kind: 'group';
  /** Stable id — also the localStorage key for open/closed. */
  readonly id: string;
  readonly label: string;
  readonly items: readonly NavItem[];
  /** Open on first visit (before the viewer has toggled it). */
  readonly defaultOpen?: boolean;
}

export type NavEntry = NavItem | NavGroup;

function item(
  id: View,
  label: string,
  icon: LucideIcon,
  activeFor?: readonly View[],
): NavItem {
  return activeFor === undefined
    ? { kind: 'item', id, label, icon }
    : { kind: 'item', id, label, icon, activeFor };
}

export const NAV: readonly NavEntry[] = [
  // Cross-account KPIs. The one page every role can open, so it sits
  // alone at the top, one click away.
  item('metrics', 'Metrics', BarChart3),
  {
    kind: 'group',
    id: 'customers',
    label: 'Customers',
    defaultOpen: true,
    items: [
      item('accounts', 'Accounts', Building2, ['account-detail']),
      // Founder request 2026-10-29 — ops2026 Agencies panel.
      item('agencies', 'Agencies', Building2),
      // Captured signup/report emails + scanned domains, claimed or not.
      item('leads', 'Leads', Mail),
      item('plans', 'Plans', Package, ['plan-new', 'plan-detail']),
      // ADR-0233 — live homepage A/B/C experiment on sill.so.
      item('experiments', 'Experiments', Split),
    ],
  },
  {
    kind: 'group',
    id: 'agents',
    label: 'Agents',
    items: [
      // ADR-0229 — agent discovery proposals / approvals.
      item('agent-registry', 'Agent registry', Bot),
      // ADR-0231 — Trust engine slice 1 (shadow verdicts + backtest).
      item('trust-engine', 'Trust engine (shadow)', ShieldCheck),
      // ADR-0235 — merchant "This is wrong" reports on trust labels.
      item('trust-disputes', 'Trust disputes', Flag),
      // ADR-0234 — AI companies enrolling their agents' keys (review queue).
      item('agent-operators', 'Agent companies', KeyRound),
    ],
  },
  {
    // Founder request 2026-10-31 — one place for every way to test
    // Sill end to end, with the paste-in checklists downloadable.
    kind: 'group',
    id: 'testing',
    label: 'Testing',
    items: [
      item('test-kits', 'Test kits', FlaskConical),
      // ADR-0176 §12 — account-scoped agent smoke-test harness board.
      item('smoke', 'Smoke Runs', HeartPulse),
      // ADR-0255 — ops2026 Protocol Test Agents.
      item('protocol-tests', 'Protocol tests', HeartPulse),
      item('testsuite', 'Test Suite', FlaskConical),
    ],
  },
  {
    kind: 'group',
    id: 'data',
    label: 'Data & compliance',
    items: [
      // ADR-0180 slice 2b — purge-run approval board.
      item('purge', 'Purge', Trash2),
      // ADR-0180 slice 3c-2 — account-deletion approval board.
      item('deletions', 'Deletions', UserX),
    ],
  },
  {
    kind: 'group',
    id: 'team',
    label: 'Team',
    items: [
      item('team', 'Team', Users),
      // Staff audit trail — who did what, when, to which account.
      item('staff-log', 'Staff log', ScrollText),
    ],
  },
  {
    kind: 'group',
    id: 'system',
    label: 'System',
    items: [
      item('health', 'System Health', Activity),
      // Scheduled-jobs runbook — durable background job health.
      item('jobs', 'Scheduled Jobs', Clock),
      // ADR-0239 — install fleet redeploy (ops-only).
      item('install-fleet', 'Install fleet', Package),
      // ADR-0245 §4.4.2 — verified-crawler review.
      item('verified-crawlers', 'Verified crawlers', Shield),
      // ADR-0251 — attack-path judgement panel review.
      item('attack-path-decisions', 'Attack path decisions', ShieldCheck),
      // ADR-0185 §2.4 — platform settings (signup breaker threshold).
      item('settings', 'Settings', SlidersHorizontal),
    ],
  },
];

/** Every link in menu order, groups flattened. */
export function flattenNav(nav: readonly NavEntry[] = NAV): NavItem[] {
  return nav.flatMap((e) => (e.kind === 'item' ? [e] : [...e.items]));
}

/** The menu as this role sees it: hidden links removed, empty groups dropped. */
export function visibleNav(
  role: StaffRole | undefined,
  nav: readonly NavEntry[] = NAV,
): NavEntry[] {
  const out: NavEntry[] = [];
  for (const e of nav) {
    if (e.kind === 'item') {
      if (canView(role, e.id)) out.push(e);
      continue;
    }
    const items = e.items.filter((i) => canView(role, i.id));
    if (items.length > 0) out.push({ ...e, items });
  }
  return out;
}

/** The link that should be highlighted for the current view, if any. */
export function activeItemFor(
  view: View,
  nav: readonly NavEntry[] = NAV,
): NavItem | undefined {
  return flattenNav(nav).find((i) => i.id === view || (i.activeFor?.includes(view) ?? false));
}

/** The group containing the current view's link, if any. */
export function activeGroupFor(
  view: View,
  nav: readonly NavEntry[] = NAV,
): NavGroup | undefined {
  const active = activeItemFor(view, nav);
  if (active === undefined) return undefined;
  return nav.find(
    (e): e is NavGroup => e.kind === 'group' && e.items.some((i) => i.id === active.id),
  );
}

// ---------------------------------------------------------------------------
// Open/closed persistence (per viewer, per browser).
// ---------------------------------------------------------------------------

export type OpenState = Readonly<Record<string, boolean>>;

const STORAGE_PREFIX = 'sill.admin.nav.open';

export function navStorageKey(staffId: string | undefined): string {
  return staffId === undefined ? STORAGE_PREFIX : `${STORAGE_PREFIX}.${staffId}`;
}

export function loadOpenState(key: string): OpenState {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
    const out: Record<string, boolean> = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof v === 'boolean') out[k] = v;
    }
    return out;
  } catch {
    // Storage blocked / private window / bad JSON — fall back to defaults.
    return {};
  }
}

export function saveOpenState(key: string, state: OpenState): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(state));
  } catch {
    /* see loadOpenState */
  }
}

/** Whether a group renders open: the viewer's choice, else its default. */
export function isGroupOpen(group: NavGroup, state: OpenState): boolean {
  return state[group.id] ?? group.defaultOpen ?? false;
}
